import {
  ASSESSMENTS_NATIVE_BRIDGE_POLL_INTERVAL_MS,
  AssessmentsNativeOutboundEventType,
} from '../constants';
import {
  AssessmentsNativeInboundEvent,
  AssessmentsNativeInboundReceipt,
  isInNativeAssessmentsWebview,
  notifyNativeApp,
} from './nativeBridge';

// `stop` is handed to the consumer directly (rather than relying on the caller having already
// stored consumeNativeBridgeQueue's return value) so it's safe to call even from the very first,
// synchronous drain below - before that return value could possibly have been assigned.
export type NativeBridgeQueueConsumer = (
  event: AssessmentsNativeInboundEvent,
  stop: () => void,
) => void;

// Polls window.__assessmentsBridgeQueue on a plain setInterval - entirely decoupled from React's
// render cycle, so nothing here re-checks the queue on every rerender. Polling only starts when
// the native app has actually registered its bridge handler (isInNativeAssessmentsWebview()); a
// normal browser tab (no handler present) never starts a timer at all.
//
// The queue is drained once immediately (synchronously, before returning) rather than waiting
// for the first interval tick, so anything already queued is picked up with no delay. Every
// subsequent drain then runs on ASSESSMENTS_NATIVE_BRIDGE_POLL_INTERVAL_MS. Each drain hands
// events to `onEvent` strictly one at a time, in arrival order, and sends an EVENT_CONSUMED event
// back to the native app after each one, acknowledging that specific event was received.
//
// Returns a stop() function. Callers must call it once they no longer need to keep polling (e.g.
// once resolved, or on unmount) - otherwise the interval keeps running for no reason.
export const consumeNativeBridgeQueue = (onEvent: NativeBridgeQueueConsumer): (() => void) => {
  if (!isInNativeAssessmentsWebview()) {
    return () => {};
  }

  let intervalId: ReturnType<typeof setInterval> | undefined;
  let stopped = false;

  const stop = () => {
    stopped = true;
    if (intervalId !== undefined) {
      clearInterval(intervalId);
    }
  };

  const drain = () => {
    // eslint-disable-next-line no-underscore-dangle -- name is dictated by the bootstrap script contract
    const queue = window.__assessmentsBridgeQueue;
    if (!queue) {
      return;
    }

    while (queue.length > 0 && !stopped) {
      const entry = queue.shift();
      if (!entry) {
        break;
      }
      const { event, resolve } = entry;
      onEvent(event, stop);
      const receipt: AssessmentsNativeInboundReceipt = {
        type: AssessmentsNativeOutboundEventType.EVENT_CONSUMED,
        consumedEventType: event.type,
        eventId: event.eventId,
      };
      // Resolves the promise postAssessmentsNativeEvent returned, so native can complete the
      // event in the same call. The EVENT_CONSUMED message is still sent for platforms that can't
      // await that promise (Android's evaluateJavascript).
      resolve(receipt);
      notifyNativeApp(receipt);
    }
  };

  drain(); // pick up anything already queued immediately, with no setInterval delay
  if (!stopped) {
    intervalId = setInterval(drain, ASSESSMENTS_NATIVE_BRIDGE_POLL_INTERVAL_MS);
  }

  return stop;
};
